<template>
  <!-- Loading State -->
  <div v-if="loading" class="flex justify-center items-center py-8">
    <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    <span class="ml-3 text-gray-600 dark:text-dark-300">Loading quotation details...</span>
  </div>

  <!-- Error State -->
  <div v-else-if="error" class="text-center py-8">
    <p class="text-red-500 dark:text-red-400">{{ error }}</p>
    <button 
      @click="fetchQuotationDetails" 
      class="mt-3 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
    >
      Retry
    </button>
  </div>

  <!-- Quotation Content -->
  <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
    <!-- Client Info -->
    <div class="bg-white dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-dark-600 transition-colors">
      <h3 class="text-base font-semibold text-gray-900 dark:text-dark-100 mb-2">Client Information</h3>
      <p class="text-sm text-gray-600 dark:text-dark-300 mb-1">{{ quotation.client_name }}</p>
      <p v-if="quotation.address" class="text-sm text-gray-600 dark:text-dark-300 mb-1">{{ quotation.address }}</p>
      <p v-if="quotation.phone" class="text-sm text-gray-600 dark:text-dark-300 mb-1">{{ quotation.phone }}</p>
      <p v-if="quotation.email" class="text-sm text-gray-600 dark:text-dark-300">{{ quotation.email }}</p>
    </div>
    
    <!-- Quotation Info -->
    <div class="bg-white dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-dark-600 transition-colors">
      <h3 class="text-base font-semibold text-gray-900 dark:text-dark-100 mb-2">Quotation Details</h3>
      <div class="flex flex-col">
        <div class="text-sm text-gray-500 dark:text-dark-400">
          <p>Date: {{ formatDate(quotation.quote_date) }}</p>
          <p>Valid Until: {{ formatDate(quotation.valid_until) }}</p>
        </div>
        
        <!-- Amount details -->
        <div class="mt-4 space-y-2">
          <div class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-dark-300">Pre-Discount:</span>
            <span class="text-gray-900 dark:text-dark-100">{{ formatPrice(parseFloat(quotation.subtotal_amount || 0)) }}</span>
          </div>
          
          <!-- Item-level discounts -->
          <div v-if="hasItemDiscounts(quotation)" class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-dark-300 italic">
              Item-level Discounts:
            </span>
            <span class="text-green-600 dark:text-green-400 font-medium">{{ formatPrice(calculateTotalItemDiscounts(quotation)) }}</span>
          </div>
          
          <!-- Overall discount -->
          <div v-if="quotation.total_discount_type && quotation.total_discount_type !== 'none'" class="flex justify-between text-sm">
            <span class="text-gray-600 dark:text-dark-300 italic">
              Added Discount:
              <span class="block text-xs">
                <template v-if="quotation.total_discount_type === 'percentage'">
                  {{ quotation.total_discount_value }}% of subtotal
                </template>
              </span>
            </span>
            <span class="text-green-600 dark:text-green-400 font-medium">{{ formatPrice(quotation.total_discount_value) }}</span>
          </div>
          
          <!-- Total discount -->
          <div v-if="(calculateTotalItemDiscounts(quotation) + parseFloat(quotation.total_discount_value || 0)) > 0" class="flex justify-between text-sm">
            <span class="text-gray-900 dark:text-dark-100 font-medium">Total Discount:</span>
            <span class="text-green-600 dark:text-green-400 font-medium">{{ formatPrice(calculateTotalItemDiscounts(quotation) + parseFloat(quotation.total_discount_value || 0)) }}</span>
          </div>
          
          <!-- Divider -->
          <div class="border-t-2 border-gray-200 dark:border-dark-600 my-2"></div>
          
          <!-- Final total -->
          <div class="flex justify-between text-base font-bold">
            <span class="text-gray-900 dark:text-dark-100">Total Amount:</span>
            <span class="text-gray-900 dark:text-dark-100">{{ formatPrice(parseFloat(quotation.subtotal_amount || 0) - parseFloat(quotation.total_discount_value || 0)) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Items Table (Desktop) -->
  <div class="hidden sm:block mt-4 bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 overflow-hidden transition-colors">
    <table class="min-w-full divide-y divide-gray-200 dark:divide-dark-600">
      <thead class="bg-gray-50 dark:bg-dark-700">
        <tr>
          <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Description</th>
          <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Qty</th>
          <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Unit Price</th>
          <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Discount</th>
          <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Pre-Discount</th>
          <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider">Total Price</th>
        </tr>
      </thead>
      <tbody class="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-600">
        <tr v-if="!quotation.items || quotation.items.length === 0">
          <td colspan="6" class="px-4 py-4 text-center text-sm text-gray-500 dark:text-dark-400">
            No items found
          </td>
        </tr>
        <tr v-else v-for="item in Array.isArray(quotation.items) ? quotation.items : []" :key="item.id" class="hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors">
          <td class="px-4 py-2 text-sm text-gray-900 dark:text-dark-100">
            {{ item.description }}
            <div v-if="item.width && item.height && item.dimension_unit && !isNaN(parseFloat(item.width)) && !isNaN(parseFloat(item.height))" class="text-xs text-gray-500 dark:text-dark-400 mt-1">
              {{ parseFloat(item.width).toFixed(2) }} {{ item.dimension_unit }} by {{ parseFloat(item.height).toFixed(2) }} {{ item.dimension_unit }}
            </div>
            <div v-if="item.unit_of_measure" class="text-xs text-gray-500 dark:text-dark-400 mt-1">
              Unit: {{ item.unit_of_measure }}
            </div>
          </td>
          <td class="px-4 py-2 text-sm text-gray-900 dark:text-dark-100 text-center">{{ item.quantity }}</td>
          <td class="px-4 py-2 text-sm text-gray-900 dark:text-dark-100 text-right">{{ formatPrice(item.unit_price) }}</td>
          <td class="px-4 py-2 text-sm text-gray-500 dark:text-dark-400 text-center">
            <span v-if="item.discount_type === 'none' || !item.discount_type">None</span>
            <span v-else-if="item.discount_type === 'percentage'">
              {{ item.discount_value || 0 }}%
              <div class="text-xs text-gray-400 dark:text-dark-500">
                ({{ formatPrice(getPreDiscountSubtotal(item) * (item.discount_value / 100)) }})
              </div>
            </span>
            <span v-else-if="item.discount_type === 'fixed'">
              {{ formatPrice(item.discount_value) }}
              <div v-if="item.quantity > 1" class="text-xs text-gray-400 dark:text-dark-500">
                ({{ item.quantity }} × ₱{{ formatPrice(item.discount_value / item.quantity) }})
              </div>
            </span>
            <span v-else>None</span>
          </td>
          <td class="px-4 py-2 text-sm text-gray-500 dark:text-dark-400 text-right">
            {{ formatPrice(getPreDiscountSubtotal(item)) }}
          </td>
          <td class="px-4 py-2 text-sm text-right" 
              :class="item.discount_type && item.discount_type !== 'none' && item.discount_value > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-dark-100'">
            {{ formatPrice(calculateItemTotalPrice(item)) }}
          </td>
        </tr>
      </tbody>
      <tfoot class="bg-gray-50 dark:bg-dark-700">
        <!-- Pre-discount total -->
        <tr>
          <td colspan="5" class="px-4 py-2 text-sm font-medium text-gray-900 dark:text-dark-100 text-right">
            Pre-Discount:
          </td>
          <td colspan="2" class="px-4 py-2 text-sm font-medium text-gray-900 dark:text-dark-100 text-right">
            {{ formatPrice(parseFloat(quotation.subtotal_amount || 0)) }}
          </td>
        </tr>
        
        <!-- Total discounts (combined) -->
        <tr v-if="(calculateTotalItemDiscounts(quotation) + parseFloat(quotation.total_discount_value || 0)) > 0">
          <td colspan="5" class="px-4 py-2 text-sm font-medium text-gray-900 dark:text-dark-100 text-right">
            Less: Total Discounts
          </td>
          <td colspan="2" class="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 text-right">
            -{{ formatPrice(calculateTotalItemDiscounts(quotation) + parseFloat(quotation.total_discount_value || 0)) }}
          </td>
        </tr>
        

        
        <tr v-if="quotation.total_discount_type && quotation.total_discount_type !== 'none'" class="text-xs text-gray-500 dark:text-dark-400">
          <td colspan="5" class="px-4 py-1 text-right italic">
            <span v-if="quotation.total_discount_type === 'percentage'">
              Overall {{ quotation.total_discount_value }}% discount
            </span>
            <span v-else>
              Overall fixed discount
            </span>
          </td>
          <td colspan="2" class="px-4 py-1 text-right italic">
            -{{ formatPrice(quotation.total_discount_value || 0) }}
          </td>
        </tr>
        
        <!-- Divider -->
        <tr class="border-t-2 border-gray-300 dark:border-dark-500">
          <td colspan="7" class="py-1"></td>
        </tr>
        
        <!-- Final total -->
        <tr class="font-bold">
          <td colspan="5" class="px-4 py-2 text-sm font-bold text-gray-900 dark:text-dark-100 text-right">
            TOTAL AMOUNT:
          </td>
          <td colspan="2" class="px-4 py-2 text-sm font-bold text-gray-900 dark:text-dark-100 text-right">
            {{ formatPrice(parseFloat(quotation.subtotal_amount || 0) - parseFloat(quotation.total_discount_value || 0)) }}
          </td>
        </tr>
      </tfoot>
    </table>
  </div>

  <!-- Items List (Mobile) -->
  <div class="block sm:hidden mt-4 space-y-3">
    <div v-if="!quotation.items || quotation.items.length === 0" class="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-4 text-center text-sm text-gray-500 dark:text-dark-400 transition-colors">
      No items found
    </div>
    <div v-else v-for="item in Array.isArray(quotation.items) ? quotation.items : []" :key="item.id" class="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-3 transition-colors">
      <div class="font-medium text-gray-900 dark:text-dark-100 mb-1">{{ item.description }}</div>
      <div v-if="item.width && item.height && item.dimension_unit && !isNaN(parseFloat(item.width)) && !isNaN(parseFloat(item.height))" class="text-xs text-gray-500 dark:text-dark-400 mb-1">
        {{ parseFloat(item.width).toFixed(2) }} {{ item.dimension_unit }} by {{ parseFloat(item.height).toFixed(2) }} {{ item.dimension_unit }}
      </div>
      <div v-if="item.unit_of_measure" class="text-xs text-gray-500 dark:text-dark-400 mb-1">
        Unit: {{ item.unit_of_measure }}
      </div>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700 dark:text-dark-300 mb-1">
        <div>Qty: <span class="font-semibold">{{ item.quantity }}</span></div>
        <div>Unit Price: <span class="font-semibold">{{ formatPrice(item.unit_price) }}</span></div>
      </div>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-700 dark:text-dark-300 mb-1">
        <div>Pre-Discount: <span class="font-semibold">{{ formatPrice(getPreDiscountSubtotal(item)) }}</span></div>
        <div>Total: <span class="font-semibold" :class="item.discount_type && item.discount_type !== 'none' && item.discount_value > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-dark-100'">{{ formatPrice(calculateItemTotalPrice(item)) }}</span></div>
      </div>
      <div class="text-xs text-gray-500 dark:text-dark-400 mb-1">
        Discount: 
        <span v-if="item.discount_type === 'none' || !item.discount_type">None</span>
        <span v-else-if="item.discount_type === 'percentage'">
          {{ item.discount_value || 0 }}% ({{ formatPrice(getPreDiscountSubtotal(item) * (item.discount_value / 100)) }})
        </span>
        <span v-else-if="item.discount_type === 'fixed'">
          {{ formatPrice(item.discount_value) }}
          <span v-if="item.quantity > 1"> ({{ item.quantity }} × ₱{{ formatPrice(item.discount_value / item.quantity) }})</span>
        </span>
        <span v-else>None</span>
      </div>
    </div>
  </div>
  
  <!-- Notes -->
  <div v-if="quotation.notes" class="mt-4 bg-white dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-dark-600 transition-colors">
    <h3 class="text-sm font-semibold text-gray-900 dark:text-dark-100 mb-2">Notes</h3>
    <p class="text-sm text-gray-600 dark:text-dark-300">{{ quotation.notes }}</p>
  </div>
  <div v-else class="mt-4 bg-white dark:bg-dark-800 p-4 rounded-lg border border-gray-200 dark:border-dark-600 transition-colors">
    <h3 class="text-sm font-semibold text-gray-900 dark:text-dark-100 mb-2">Notes</h3>
    <p class="text-sm text-gray-500 dark:text-dark-400 italic">No notes provided</p>
  </div>

  <!-- Approval Actions (only shown when opened from approval context) -->
  <div v-if="isApprovalContext && quotation && hasPermission('quotations.approve')" class="mt-6 pt-6 border-t border-gray-200 dark:border-dark-600">
    <div class="flex flex-col sm:flex-row gap-3">
      <button
        @click="showApprovalModal"
        :disabled="isProcessingApproval"
        class="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-check"></i>
        {{ isProcessingApproval ? 'Approving...' : 'Approve Quotation' }}
      </button>
      <button
        @click="handleReject"
        :disabled="isProcessingApproval"
        class="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin"></i>
        <i v-else class="fas fa-times"></i>
        {{ isProcessingApproval ? 'Processing...' : 'Reject Quotation' }}
      </button>
    </div>
  </div>

  <!-- Email Confirmation Modal -->
  <div v-if="showEmailModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0 relative z-50">
      <div class="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity z-40" aria-hidden="true" @click="closeEmailModal"></div>
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
      <div class="inline-block align-bottom bg-white dark:bg-dark-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full relative z-50">
        <div class="bg-white dark:bg-dark-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 sm:mx-0 sm:h-10 sm:w-10">
              <i class="fas fa-envelope text-green-600 dark:text-green-400"></i>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-dark-100" id="modal-title">
                Approve Quotation
              </h3>
              <div class="mt-4">
                <p class="text-sm text-gray-500 dark:text-dark-400 mb-4">
                  You are about to approve this quotation. Would you like to send an email notification to the client?
                </p>
                <div class="flex items-center">
                  <input
                    type="checkbox"
                    id="sendEmailCheckbox"
                    v-model="sendEmailChoice"
                    class="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  >
                  <label for="sendEmailCheckbox" class="ml-2 block text-sm text-gray-900 dark:text-dark-100">
                    Send email notification to client
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 dark:bg-dark-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2">
          <button
            type="button"
            @click="confirmApprove"
            :disabled="isProcessingApproval"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i v-if="isProcessingApproval" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isProcessingApproval ? 'Approving...' : 'Approve' }}
          </button>
          <button
            type="button"
            @click="closeEmailModal"
            :disabled="isProcessingApproval"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-dark-600 shadow-sm px-4 py-2 bg-white dark:bg-dark-800 text-base font-medium text-gray-700 dark:text-dark-200 hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useApprovalFormatting } from '@/composables/useApprovalFormatting';
import { useApi } from '@/composables/useApi';
import { useAuth } from '@/composables/useAuth';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  quotation: {
    type: Object,
    required: true
  },
  quotationId: {
    type: [Number, String],
    required: false
  },
  isApprovalContext: {
    type: Boolean,
    default: false
  }
});

// Define emits for approval events
const emit = defineEmits(['approved', 'rejected']);

// Initialize composables
const api = useApi();
const { hasPermission } = useAuth();
const toast = useToast();

// Component state
const loading = ref(false);
const error = ref(null);
const quotationData = ref(null);

// Approval state
const isProcessingApproval = ref(false);
const showEmailModal = ref(false);
const sendEmailChoice = ref(true);

const { formatDate, formatPrice, hasItemDiscounts, calculateTotalItemDiscounts } = useApprovalFormatting();

// Computed property to get the quotation data to use
const quotation = computed(() => {
  return quotationData.value || props.quotation;
});

// Check if we need to fetch full quotation details
const needsFullData = computed(() => {
  const q = props.quotation;
  // Check if the quotation object is missing key detailed fields
  return !q.items || !Array.isArray(q.items) || q.items.length === 0 || !q.quote_date;
});

// Fetch full quotation details
const fetchQuotationDetails = async () => {
  const quotationId = props.quotationId || props.quotation.id;
  if (!quotationId) {
    error.value = 'No quotation ID provided';
    return;
  }

  try {
    loading.value = true;
    error.value = null;
    
    console.log('Fetching full quotation details for ID:', quotationId);
    const response = await api.get(`/quotations/${quotationId}`);
    
    if (response.data && response.data.data) {
      quotationData.value = response.data.data;
      console.log('Quotation details loaded:', response.data.data);
    } else if (response.data) {
      // Fallback if data is directly in response.data
      quotationData.value = response.data;
      console.log('Quotation details loaded (fallback):', response.data);
    } else {
      throw new Error('No quotation data received');
    }
  } catch (err) {
    console.error('Error loading quotation details:', err);
    error.value = err.response?.data?.message || 'Failed to load quotation details';
  } finally {
    loading.value = false;
  }
};

// Calculate true pre-discount subtotal by summing pre_discount_subtotal values
const calculatePreDiscountSubtotal = (quotation) => {
  if (!quotation?.items || !Array.isArray(quotation.items) || quotation.items.length === 0) {
    return 0;
  }
  
  return quotation.items.reduce((total, item) => {
    return total + parseFloat(item.pre_discount_subtotal || 0);
  }, 0);
};

// Calculate the correct discounted price per item
const calculateItemTotalPrice = (item) => {
  const preDiscountSubtotal = parseFloat(item.pre_discount_subtotal || 0);
  
  if (!item.discount_type || item.discount_type === 'none' || !item.discount_value) {
    return preDiscountSubtotal;
  }
  
  let discountAmount = 0;
  if (item.discount_type === 'percentage') {
    discountAmount = (preDiscountSubtotal * parseFloat(item.discount_value)) / 100;
  } else if (item.discount_type === 'fixed') {
    discountAmount = parseFloat(item.discount_value || 0);
  }
  
  return Math.max(preDiscountSubtotal - discountAmount, 0);
};

// Helper function to calculate pre-discount subtotal with dimensions support
const getPreDiscountSubtotal = (item) => {
  if (item.pre_discount_subtotal && parseFloat(item.pre_discount_subtotal) > 0) {
    return parseFloat(item.pre_discount_subtotal);
  }
  
  // Fallback calculation
  let baseSubtotal = item.quantity * item.unit_price;
  
  // If item has dimensions, multiply by area
  if (item.width && item.height && !isNaN(parseFloat(item.width)) && !isNaN(parseFloat(item.height))) {
    const area = parseFloat(item.width) * parseFloat(item.height);
    baseSubtotal = item.quantity * item.unit_price * area;
  }
  
  return baseSubtotal;
};

// Modal handlers
const showApprovalModal = () => {
  sendEmailChoice.value = true; // Default to checked
  showEmailModal.value = true;
};

const closeEmailModal = () => {
  if (!isProcessingApproval.value) {
    showEmailModal.value = false;
  }
};

// Approval handlers
const confirmApprove = async () => {
  if (!quotation.value || isProcessingApproval.value) return;
  
  const quotationId = props.quotationId || quotation.value.id;
  if (!quotationId) return;
  
  try {
    isProcessingApproval.value = true;
    
    const response = await api.post(`/quotations/${quotationId}/approve`, {
      status: 'approved',
      approved_by: 'current_user', // This should be handled by the backend
      sendEmail: sendEmailChoice.value
    });
    
    if (response.data) {
      const emailMessage = sendEmailChoice.value 
        ? 'Quotation approved and email sent successfully' 
        : 'Quotation approved successfully (no email sent)';
      toast.success(emailMessage);
      
      // Close modal and emit event to parent component
      showEmailModal.value = false;
      emit('approved', quotation.value);
    }
  } catch (error) {
    console.error('Error approving quotation:', error);
    toast.error(error.response?.data?.message || 'Failed to approve quotation');
  } finally {
    isProcessingApproval.value = false;
  }
};

const handleReject = async () => {
  if (!quotation.value || isProcessingApproval.value) return;
  
  const quotationId = props.quotationId || quotation.value.id;
  if (!quotationId) return;
  
  const reason = prompt('Please provide a reason for rejecting this quotation:');
  if (!reason || reason.trim() === '') {
    toast.warning('Please provide a reason for rejection');
    return;
  }
  
  try {
    isProcessingApproval.value = true;
    
    const response = await api.post(`/quotations/${quotationId}/reject`, {
      status: 'rejected',
      rejection_reason: reason.trim(),
      rejected_by: 'current_user' // This should be handled by the backend
    });
    
    if (response.data) {
      toast.success('Quotation rejected successfully');
      
      // Emit event to parent component to refresh the approvals list
      emit('rejected', quotation.value);
    }
  } catch (error) {
    console.error('Error rejecting quotation:', error);
    toast.error(error.response?.data?.message || 'Failed to reject quotation');
  } finally {
    isProcessingApproval.value = false;
  }
};

// Load full quotation details on mount if needed
onMounted(() => {
  if (needsFullData.value) {
    fetchQuotationDetails();
  }
});
</script> 